// hooks/useVisaPacksByCountry.ts
import { useEffect, useState } from 'react';
import axios from 'axios';
import { VisaPlan } from '../visas/visa-page-layout';
import { VisaPack } from '../types/visa';
import { API_URL } from '../config';

// NOTE: aligned with VisaApplication's fetching pattern (axios + API_URL from
// ../config) instead of a relative fetch('/api/...'). A relative path only
// works when the frontend dev server proxies /api to Laravel — if it doesn't,
// the request resolves against the frontend's own origin and you get back
// index.html instead of JSON (the "Unexpected token '<'" error).

interface ApiResponse {
  status: boolean;
  message?: string;
  country?: { id: number; name: string; code: string };
  data: VisaPack[];
}

export function useVisaPacksByCountry(countryCode: string) {
  const [plans, setPlans] = useState<VisaPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get<ApiResponse>(
          `${API_URL}/visa-packs/by-country/${countryCode}`,
          { headers: { Accept: 'application/json' } }
        );

        const json = res.data;

        if (!json.status) {
          throw new Error(json.message || 'Failed to fetch visa packs');
        }

        const mapped: VisaPlan[] = json.data.map((p, i) => ({
          id: String(p.id),
          name: p.name_en,
          entryType: p.entry_type,
          validity: p.visa_validity_en,
          stay: p.stay_validity_en,
          processing: p.processing_time_en,
          price: String(p.price),
          currency: p.currency,
          featured: i === Math.floor((json.data.length - 1) / 2),
          // Keep the full API record so the "Apply Now" click can hand the
          // exact VisaPack shape VisaApplication expects (destination_country,
          // visa_type, entry_type, processing_time_en, etc.) without a second
          // fetch or a reshuffled mapping.
          raw: p,
        }));

        if (!cancelled) setPlans(mapped);
      } catch (e: any) {
        const serverMessage = e?.response?.data?.message || e?.message;
        if (!cancelled) setError(serverMessage || 'Failed to fetch visa packs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  return { plans, loading, error };
}